/**
 * useSupportUnreadCount Hook
 *
 * Polls the backend for the unread support-ticket count and exposes it to
 * the navigation badge components (user nav + admin nav).
 *
 * Behaviour:
 * - Lazily fetches once on mount.
 * - Re-fetches on a configurable polling interval (default 60s) so badges
 *   stay roughly fresh while a user is on the page without hammering the
 *   server.
 * - Re-fetches when the tab regains focus (visibilitychange) so users who
 *   come back from email don't see a stale "0".
 * - Exposes a `refresh()` callback for code paths that mutate read-state
 *   themselves (e.g. opening a ticket clears its unread flag, so the
 *   detail page can call `refresh()` to recompute the badge immediately).
 *
 * The hook is shared between sides — the caller passes the appropriate
 * fetcher function so the same logic powers both the user inbox and the
 * admin nav. Errors are swallowed and logged: a transient API failure
 * should never break the badge.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  subscribeSupportUnreadCountRefresh,
  type SupportUnreadCountScope,
} from '../utils/supportUnreadCountSync';

/** Default polling interval in milliseconds. */
const DEFAULT_POLL_INTERVAL_MS = 60_000;

export interface UseSupportUnreadCountOptions {
  /**
   * The async function that fetches the unread count from the backend.
   * Pass `getMySupportUnreadCount` for the user side or
   * `getAdminSupportUnreadCount` for the admin side.
   */
  fetcher: () => Promise<number>;

  /**
   * Poll interval in milliseconds. Defaults to 60s. Pass `0` to disable
   * polling and only refetch on mount / visibility / explicit refresh.
   */
  pollIntervalMs?: number;

  /**
   * When false, the hook stays idle and reports `0`. Used by the user
   * variant to avoid polling while the user is unauthenticated and by the
   * admin variant to avoid polling for non-admin users.
   */
  enabled?: boolean;

  /**
   * When set, {@link requestSupportUnreadCountRefresh} for this scope
   * triggers an immediate re-fetch (e.g. after opening a ticket thread).
   */
  syncScope?: SupportUnreadCountScope;
}

export interface UseSupportUnreadCountResult {
  /** Current unread count; `0` while loading or when disabled. */
  count: number;

  /** True the very first time the hook is fetching (no count yet). */
  isLoading: boolean;

  /**
   * Forces an immediate re-fetch. Useful after the caller knows the count
   * has changed (e.g. opening a ticket clears its unread flag server-side,
   * so the badge should refresh right away instead of waiting for the
   * next poll tick).
   */
  refresh: () => void;
}

/**
 * Generic unread-count poller used by both the user and admin nav badges.
 */
export const useSupportUnreadCount = ({
  fetcher,
  pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
  enabled = true,
  syncScope,
}: UseSupportUnreadCountOptions): UseSupportUnreadCountResult => {
  const [count, setCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const hasLoadedRef = useRef(false);

  const refresh = useCallback(async () => {
    if (!enabled) {
      return;
    }

    if (!hasLoadedRef.current) {
      setIsLoading(true);
    }

    try {
      const next = await fetcherRef.current();
      setCount(next);
    } catch (error) {
      console.warn('[useSupportUnreadCount] failed to fetch unread count', error);
    } finally {
      hasLoadedRef.current = true;
      setIsLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      setCount(0);
      hasLoadedRef.current = false;
      return;
    }

    refresh();

    // Polling: only when the document is visible to avoid background-tab
    // load on the API.
    let intervalId: ReturnType<typeof setInterval> | null = null;
    if (pollIntervalMs > 0) {
      intervalId = setInterval(() => {
        if (document.visibilityState === 'visible') {
          refresh();
        }
      }, pollIntervalMs);
    }

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        refresh();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      if (intervalId !== null) {
        clearInterval(intervalId);
      }
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [enabled, pollIntervalMs, refresh]);

  useEffect(() => {
    if (!enabled || !syncScope) {
      return;
    }

    return subscribeSupportUnreadCountRefresh(syncScope, () => {
      void refresh();
    });
  }, [enabled, syncScope, refresh]);

  return {
    count,
    isLoading,
    refresh: () => {
      void refresh();
    },
  };
};

/**
 * Convenience wrapper for the authenticated user side. Disables itself
 * automatically when the user is unauthenticated.
 */
export const useMySupportUnreadCount = (
  fetcher: () => Promise<number>,
  pollIntervalMs?: number
): UseSupportUnreadCountResult => {
  const { isAuthenticated, termsAccepted } = useAuth();
  return useSupportUnreadCount({
    fetcher,
    pollIntervalMs,
    enabled: isAuthenticated && termsAccepted,
    syncScope: 'user',
  });
};

/**
 * Convenience wrapper for the admin side. Disables itself automatically
 * when the caller is not in an admin role.
 */
export const useAdminSupportUnreadCount = (
  fetcher: () => Promise<number>,
  pollIntervalMs?: number
): UseSupportUnreadCountResult => {
  const { isAuthenticated, isAdmin } = useAuth();
  return useSupportUnreadCount({
    fetcher,
    pollIntervalMs,
    enabled: isAuthenticated && isAdmin,
    syncScope: 'admin',
  });
};
